import { useCallback, useEffect, useState } from "react";
import { LogOut, Shield, RefreshCw } from "lucide-react";
import Navbar from "@/components/luxenova/Navbar";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "luxenova.admin.token";

const TABS = [
    { id: "summary", label: "Overview" },
    { id: "leads", label: "Leads" },
    { id: "bookings", label: "Bookings" },
    { id: "maintenance", label: "Maintenance" },
    { id: "consultations", label: "Consultations" },
    { id: "contacts", label: "Enquiries" },
    { id: "inventory", label: "Inventory" },
    { id: "sessions", label: "Agent Sessions" },
];

const TABLE_CONFIG = {
    leads: {
        endpoint: "/admin/leads",
        columns: ["created_at", "name", "email", "phone", "interest", "score", "status"],
        statusOptions: ["new", "contacted", "qualified", "lost"],
    },
    bookings: {
        endpoint: "/admin/bookings",
        columns: ["created_at", "guest_name", "property_title", "check_in", "check_out", "guests", "status"],
        statusOptions: ["pending", "confirmed", "cancelled"],
    },
    maintenance: {
        endpoint: "/admin/maintenance",
        columns: ["created_at", "property_title", "priority", "description", "contact_name", "status"],
        statusOptions: ["open", "in_progress", "closed"],
    },
    contacts: {
        endpoint: "/admin/contact-submissions",
        columns: ["created_at", "name", "email", "service", "description", "email_sent"],
    },
    consultations: {
        endpoint: "/admin/consultations",
        columns: ["date", "time_slot", "name", "email", "phone", "consultation_type", "market", "notes", "status"],
        statusOptions: ["pending", "confirmed", "completed", "cancelled"],
    },
};

export default function Admin() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [tab, setTab] = useState("summary");

    if (!token) return <LoginScreen onAuth={(t) => { sessionStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
    const signOut = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); };

    return (
        <div className="min-h-screen bg-[#050D1C] text-slate-200">
            <Navbar />
            <main className="pt-28 pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                        <div>
                            <span className="eyebrow">Admin</span>
                            <h1 className="font-serif-display text-white text-4xl mt-4">LuxeNova Command Centre</h1>
                            <p className="mt-3 text-slate-400 text-sm max-w-xl">
                                Everything the AI concierge has captured for the team — leads, bookings, maintenance, enquiries, inventory and sessions.
                            </p>
                        </div>
                        <button data-testid="admin-signout" onClick={signOut} className="btn-ghost-gold self-start md:self-end">
                            <LogOut size={14} /> Sign out
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-10 border-b border-[#C9A96E]/15 pb-3">
                        {TABS.map((tt) => (
                            <button
                                key={tt.id}
                                data-testid={`admin-tab-${tt.id}`}
                                onClick={() => setTab(tt.id)}
                                className={`px-4 py-2 text-[11px] tracking-[0.24em] uppercase transition-colors ${
                                    tab === tt.id
                                        ? "text-[#C9A96E] border-b-2 border-[#C9A96E]"
                                        : "text-slate-400 hover:text-[#C9A96E]"
                                }`}
                            >
                                {tt.label}
                            </button>
                        ))}
                    </div>

                    {tab === "summary" && <Summary token={token} />}
                    {tab === "leads" && <TableSection token={token} kind="leads" {...TABLE_CONFIG.leads} />}
                    {tab === "bookings" && <TableSection token={token} kind="bookings" {...TABLE_CONFIG.bookings} />}
                    {tab === "maintenance" && <TableSection token={token} kind="maintenance" {...TABLE_CONFIG.maintenance} />}
                    {tab === "consultations" && <TableSection token={token} kind="consultations" {...TABLE_CONFIG.consultations} />}
                    {tab === "contacts" && <TableSection token={token} kind="contacts" {...TABLE_CONFIG.contacts} />}
                    {tab === "inventory" && <InventoryPanel token={token} />}
                    {tab === "sessions" && <SessionsPanel token={token} />}
                </div>
            </main>
        </div>
    );
}

function LoginScreen({ onAuth }) {
    const [pwd, setPwd] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await fetch(`${API}/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password: pwd }),
            });
            if (!res.ok) throw new Error("Invalid credentials");
            const { token } = await res.json();
            onAuth(token);
            toast.success("Welcome to LuxeNova Command Centre.");
        } catch (err) {
            toast.error(err.message || "Login failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050D1C] flex items-center justify-center px-6">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="glass-card p-10 w-full max-w-md text-center"
            >
                <img src={BRAND.logo} alt="LuxeNova" className="mx-auto h-16 w-16 rounded-full ring-1 ring-[#C9A96E]/40" />
                <span className="eyebrow mt-6 block">Admin Portal</span>
                <h1 className="font-serif-display text-white text-3xl mt-3">
                    <Shield size={20} className="inline text-[#C9A96E] -mt-1 mr-2" />
                    Command Centre
                </h1>
                <p className="mt-3 text-slate-400 text-sm">Enter your admin password to continue.</p>

                <input
                    data-testid="admin-password-input"
                    type="password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="Admin password"
                    className="mt-8 w-full bg-[#0A1A38]/60 border border-[#C9A96E]/25 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#C9A96E]/70 rounded-sm px-4 py-3 text-sm"
                />

                <button data-testid="admin-login-submit" disabled={busy} className="btn-gold mt-6 w-full justify-center">
                    {busy ? "Signing in…" : "Sign in"}
                </button>
                <p className="mt-6 text-[10px] tracking-[0.24em] uppercase text-slate-500">
                    Contact LuxeNova ops if you don't have credentials.
                </p>
            </form>
        </div>
    );
}

function Summary({ token }) {
    const [data, setData] = useState(null);
    const load = useCallback(() => fetch(`${API}/admin/summary`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.ok ? r.json() : null).then(setData), [token]);
    useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [load]);

    if (!data) return <Loader />;
    const cards = [
        { k: "Hot Leads", v: data.hot_leads, testId: "kpi-hot-leads", tone: "gold" },
        { k: "Urgent Maintenance", v: data.urgent_maintenance, testId: "kpi-urgent-maintenance", tone: "gold" },
        { k: "Total Leads", v: data.leads, testId: "kpi-total-leads" },
        { k: "Bookings", v: data.bookings, testId: "kpi-bookings" },
        { k: "Maintenance", v: data.maintenance_requests, testId: "kpi-maintenance" },
        { k: "Enquiries", v: data.contact_submissions, testId: "kpi-enquiries" },
        { k: "Properties", v: data.properties, testId: "kpi-properties" },
        { k: "Inventory items", v: data.inventory, testId: "kpi-inventory" },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {cards.map((c) => (
                <div key={c.k} data-testid={c.testId} className="glass-card p-6">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-slate-400">{c.k}</div>
                    <div className={`mt-3 font-serif-display text-4xl ${c.tone === "gold" ? "text-[#C9A96E]" : "text-white"}`}>{c.v ?? 0}</div>
                </div>
            ))}
        </div>
    );
}

function TableSection({ token, kind, endpoint, columns, statusOptions }) {
    const [rows, setRows] = useState(null);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        const url = new URL(`${API}${endpoint}`);
        if (filter !== "all" && statusOptions) {
            if (kind === "leads") url.searchParams.set("score", filter);
            else url.searchParams.set("status", filter);
        }
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        setRows(res.ok ? await res.json() : []);
    }, [token, filter, endpoint, kind, statusOptions]);
    useEffect(() => { load(); }, [load]);

    const patch = async (id, body) => {
        await fetch(`${API}${endpoint}/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
        });
        load();
    };

    if (rows === null) return <Loader />;
    return (
        <div>
            {statusOptions && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                    <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>All</FilterChip>
                    {kind === "leads"
                        ? ["hot", "warm", "cold"].map((f) => (
                            <FilterChip key={f} active={filter === f} onClick={() => setFilter(f)}>{f}</FilterChip>
                        ))
                        : statusOptions.map((s) => (
                            <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)}>{s}</FilterChip>
                        ))}
                    <button onClick={load} className="ml-auto text-slate-400 hover:text-[#C9A96E] text-xs uppercase tracking-widest flex items-center gap-1">
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>
            )}

            <div className="glass-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid={`admin-table-${kind}`}>
                        <thead className="bg-[#0A1A38] border-b border-[#C9A96E]/20">
                            <tr>
                                {columns.map((c) => (
                                    <th key={c} className="text-left px-4 py-3 text-[10px] tracking-[0.24em] uppercase text-slate-400 font-normal">
                                        {c.replace(/_/g, " ")}
                                    </th>
                                ))}
                                {statusOptions && <th className="text-left px-4 py-3 text-[10px] tracking-[0.24em] uppercase text-slate-400 font-normal">Change</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500">
                                        No records.
                                    </td>
                                </tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`admin-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                                    {columns.map((c) => (
                                        <td key={c} className="px-4 py-3 align-top text-slate-300 whitespace-normal max-w-[240px] break-words">
                                            {renderCell(r, c)}
                                        </td>
                                    ))}
                                    {statusOptions && (
                                        <td className="px-4 py-3">
                                            <select
                                                value={r.status || ""}
                                                data-testid={`admin-status-${r.id}`}
                                                onChange={(e) => patch(r.id, { status: e.target.value })}
                                                className="bg-[#0A1A38] border border-[#C9A96E]/25 text-slate-100 text-xs px-2 py-1 rounded-sm"
                                            >
                                                {statusOptions.map((o) => (
                                                    <option key={o} value={o}>{o}</option>
                                                ))}
                                            </select>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function renderCell(row, col) {
    const v = row[col];
    if (v == null || v === "") return <span className="text-slate-600">—</span>;
    if (col === "created_at" || col === "check_in" || col === "check_out") {
        try { return new Date(v).toLocaleString(); } catch (_) { return String(v); }
    }
    if (col === "score") {
        const tone = v === "hot" ? "bg-[#C9A96E] text-[#050D1C]" : v === "warm" ? "bg-[#C9A96E]/25 text-[#C9A96E]" : "bg-slate-700 text-slate-300";
        return <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${tone}`}>{v}</span>;
    }
    if (col === "priority") {
        const tone = v === "urgent" ? "bg-red-500/20 text-red-300 border border-red-400/40" : "bg-slate-700/40 text-slate-300 border border-slate-500/40";
        return <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${tone}`}>{v}</span>;
    }
    if (col === "email_sent") return v ? "✓" : "—";
    return String(v);
}

function InventoryPanel({ token }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        fetch(`${API}/admin/inventory`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : []).then(setRows);
    }, [token]);
    if (rows === null) return <Loader />;
    return (
        <div className="glass-card overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-table-inventory">
                <thead className="bg-[#0A1A38] border-b border-[#C9A96E]/20">
                    <tr>
                        {["property_id", "item", "quantity", "unit", "reorder_level", "notes"].map((c) => (
                            <th key={c} className="text-left px-4 py-3 text-[10px] tracking-[0.24em] uppercase text-slate-400 font-normal">{c.replace(/_/g, " ")}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className={`border-b border-white/5 ${r.quantity < r.reorder_level ? "bg-red-500/5" : ""}`}>
                            <td className="px-4 py-3 text-slate-300">{r.property_id}</td>
                            <td className="px-4 py-3 text-white">{r.item}</td>
                            <td className="px-4 py-3 text-slate-300">{r.quantity}</td>
                            <td className="px-4 py-3 text-slate-400">{r.unit}</td>
                            <td className="px-4 py-3 text-slate-400">{r.reorder_level}</td>
                            <td className="px-4 py-3 text-slate-400">{r.notes || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SessionsPanel({ token }) {
    const [sessions, setSessions] = useState(null);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        fetch(`${API}/admin/agent-sessions`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : []).then(setSessions);
    }, [token]);

    useEffect(() => {
        if (!selected) return setDetail(null);
        fetch(`${API}/admin/agent-sessions/${selected}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : null).then(setDetail);
    }, [selected, token]);

    if (sessions === null) return <Loader />;
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="glass-card overflow-hidden lg:col-span-1">
                <div className="max-h-[600px] overflow-y-auto divide-y divide-white/5">
                    {sessions.length === 0 && <div className="p-6 text-center text-slate-500">No sessions.</div>}
                    {sessions.map((s) => (
                        <button
                            key={s.session_id}
                            onClick={() => setSelected(s.session_id)}
                            className={`w-full text-left px-5 py-4 hover:bg-white/[0.03] ${selected === s.session_id ? "bg-white/[0.04]" : ""}`}
                        >
                            <div className="text-white text-sm font-serif-display">{s.session_id.slice(0, 8)}…</div>
                            <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-1">
                                {s.message_count} messages · {new Date(s.created_at).toLocaleString()}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
            <div className="glass-card p-6 lg:col-span-2 max-h-[600px] overflow-y-auto">
                {!detail && <div className="text-slate-500 text-sm">Select a session to view its transcript.</div>}
                {detail && (
                    <div className="space-y-3">
                        {(detail.messages || []).map((m, i) => (
                            <div key={`${detail.session_id || selected}-${i}-${m.role}`} className={`text-sm rounded-sm px-4 py-3 ${
                                m.role === "user" ? "bg-[#C9A96E]/10 border border-[#C9A96E]/25" :
                                m.role === "assistant" ? "bg-white/[0.03] border border-white/10" :
                                "bg-black/30 border border-white/10 text-xs font-mono"
                            }`}>
                                <div className="text-[10px] tracking-widest uppercase text-slate-500 mb-1">{m.role}</div>
                                <div className="text-slate-200 whitespace-pre-wrap break-words">{m.content}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function FilterChip({ active, children, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border rounded-sm transition ${
                active
                    ? "border-[#C9A96E] bg-[#C9A96E] text-[#050D1C]"
                    : "border-[#C9A96E]/25 text-slate-300 hover:border-[#C9A96E]/60 hover:text-[#C9A96E]"
            }`}
        >
            {children}
        </button>
    );
}

function Loader() {
    return <div className="glass-card p-10 text-center text-slate-500 text-sm tracking-widest uppercase">Loading…</div>;
}
